import { SpaceBetween } from '@amzn/awsui-components-core';
import Box from '@amzn/awsui-components-core/box';
import Link from '@amzn/awsui-components-core/link';
import { useBundle } from '@amzn/react-arb-tools';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import 'src/styles/dfp-home-page/welcome-banner.scss';
import promoArtDark from 'src/styles/landing-page/welcome-banner-promo-art-dark.svg';
import promoArtLight from 'src/styles/landing-page/welcome-banner-promo-art-light.svg';
import FxRateCard from './cards/FxRateCard';
import NewsTickerCard from './cards/NewsTickerCard';
import NflScoresCard from './cards/NflScoresCard';
import StockCard from './cards/StockCard';
import WeatherCard from './cards/WeatherCard';
import { useWelcomeBannerData } from './hooks/useWelcomeBannerData';
import { WELCOME_BANNER } from './landing-page-constants';
import { useUserPreferences } from '../top-navigation/user-preferences/useUserPreferences';

/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Welcome banner — being rebuilt. All data logic lives in useWelcomeBannerData.
 */
const WelcomeBanner: React.FC = () => {
  const { GivenName, DisplayName } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [bundle] = useBundle('pages.landing');

  const t = (key: string) => {
    try {
      return bundle?.getMessage(key) ?? key;
    } catch {
      return key;
    }
  };
  const tf = (key: string, args: Record<string, string>) => {
    try {
      return bundle?.formatMessage(key, args) ?? key;
    } catch {
      return key;
    }
  };

  const firstName = GivenName || DisplayName?.split(' ')[0] || '';
  const promoArt = isDarkMode ? promoArtDark : promoArtLight;

  const { weather, stock, fxConfig, fxIndex, setFxIndex, tempUnit, setTempUnit, newsItems, newsIndex, newsHovered, setNewsHovered, nflGames } =
    useWelcomeBannerData();
  const { preferences } = useUserPreferences();
  const { cardVisibility } = preferences;

  return (
    <Box>
      {/* Welcome Content  */}
      <Box
        nativeAttributes={{
          className: 'landing-welcome-banner__content',
          style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflowX: 'auto' }
        }}
      >
        <Box nativeAttributes={{ style: { flexShrink: 0 } }}>
          <span className="landing-welcome-banner__welcome">{t('welcome_greeting')}</span>{' '}
          <span className="landing-welcome-banner__name">{firstName}</span>
        </Box>

        <Box nativeAttributes={{ style: { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 } }}>
          <Box textAlign="right" padding={{ left: 'm' }}>
            <SpaceBetween size="xs" direction="vertical">
              <Box variant="h3" fontSize="heading-m" fontWeight="bold">
                {t('promo_title')}
              </Box>
              <Box variant="p" nativeAttributes={{ style: { whiteSpace: 'nowrap' } }}>
                {t('promo_line1')}
                <SpaceBetween size="xxs" direction="horizontal">
                  {t('promo_line2a')} {t('promo_line2b')}
                </SpaceBetween>
                <Link
                  href={WELCOME_BANNER.promoLinkHref}
                  fontSize="body-s"
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(WELCOME_BANNER.promoLinkHref);
                  }}
                >
                  {t('promo_link_text')}
                </Link>
              </Box>
            </SpaceBetween>
          </Box>

          <img className="landing-welcome-banner__promo-art" src={promoArt} alt="" aria-hidden="true" />
        </Box>
      </Box>

      {/* Welcome Cards */}
      <div className="landing-welcome-banner__cards" role="region" aria-label={t('cards_region_aria')}>
        {cardVisibility.weather && weather && (
          <WeatherCard weather={weather} tempUnit={tempUnit} onTempUnitChange={setTempUnit} ariaLabel={t('weather_card_aria')} />
        )}
        {cardVisibility.stock && stock && <StockCard stock={stock} ariaLabel={t('stock_card_aria')} />}
        {cardVisibility.fxRates && fxConfig && fxConfig.pairs.length > 0 && (
          <FxRateCard
            fxConfig={fxConfig}
            fxIndex={fxIndex}
            onFxIndexChange={setFxIndex}
            ariaLabel={t('fx_card_aria')}
            label={tf('fx_card_label', { baseCurrency: fxConfig.baseCurrency })}
          />
        )}
        {cardVisibility.newsTicker && newsItems.length > 0 && (
          <NewsTickerCard
            newsItem={newsItems[newsIndex]}
            onMouseEnter={() => setNewsHovered(true)}
            onMouseLeave={() => setNewsHovered(false)}
            ariaLabel={t('news_card_aria')}
          />
        )}
        {cardVisibility.nflScores && nflGames.length > 0 && <NflScoresCard games={nflGames} />}
      </div>
    </Box>
  );
};

export default WelcomeBanner;
